import PropTypes from 'prop-types';
import { useEffect, forwardRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { m } from 'framer-motion';
import { NAVBAR } from '../../config';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Drawer, Stack, IconButton, Badge } from '@mui/material';
import useResponsive from '../../hooks/useResponsive';
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import DrawerMenuListView from '../../components/DrawerMenuListView';
import { useAppSelector, useAppDispatch } from '../../store';
import { logoutUser } from '../../store/auth/api';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import cssStyles from '../../utils/cssStyles';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import HomeIcon from '@mui/icons-material/Home';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { FaPeopleGroup } from 'react-icons/fa6';
import { TbCategoryFilled } from 'react-icons/tb';
import { MdAddBusiness } from 'react-icons/md';
import { MdOutlinePostAdd } from 'react-icons/md';
import { RiCake3Fill } from 'react-icons/ri';
import { IoIosNotifications } from 'react-icons/io';
import { fetchPendingOrdersCount, refreshPendingOrdersCount } from '../../store/pendingOrdersCount/pendingOrdersCountSlice';
import { isPartnerApiEnabled } from '../../utils/partnerFeature';



const DRAWER_WIDTH = 280;

const pendingOrderBadgeSx = {
  '& .MuiBadge-badge': {
    height: 18,
    minWidth: 18,
    padding: '0 4px',
    borderRadius: '9px',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '18px',
    letterSpacing: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
};

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

const IconButtonAnimate = forwardRef(
  ({ children, size = 'medium', ...other }: any, ref) => (
    <AnimateWrap size={size}>
      <IconButton
        size={size}
        ref={ref}
        {...other}
      >
        {children}
      </IconButton>
    </AnimateWrap>
  )
);

IconButtonAnimate.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.oneOf([
    'inherit',
    'default',
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};
const varSmall = {
  hover: { scale: 1.1 },
  tap: { scale: 0.95 },
};

const varMedium = {
  hover: { scale: 1.09 },
  tap: { scale: 0.97 },
};

const varLarge = {
  hover: { scale: 1.08 },
  tap: { scale: 0.99 },
};

AnimateWrap.propTypes = {
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

function AnimateWrap({ size, children }: { size: any; children: any }) {
  const isSmall = size === 'small';
  const isLarge = size === 'large';

  return (
    <Box
      component={m.div}
      whileTap='tap'
      whileHover='hover'
      variants={(isSmall && varSmall) || (isLarge && varLarge) || varMedium}
      sx={{
        display: 'inline-flex',
      }}
    >
      {children}
    </Box>
  );
}

function CollapseButton({
  onToggleCollapse,
  collapseClick,
}: {
  onToggleCollapse?: any;
  collapseClick?: any;
}) {
  return (
    <IconButtonAnimate onClick={onToggleCollapse}>
      <Box
        sx={{
          lineHeight: 0,
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          ...(collapseClick && {
            transform: 'rotate(180deg)',
          }),
        }}
      >
        <KeyboardDoubleArrowLeftIcon />
      </Box>
    </IconButtonAnimate>
  );
}

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
}: {
  isOpenSidebar: boolean;
  onCloseSidebar: () => void;
}) {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { userRole, id: _id } = useAppSelector((state) => state.auth.user);
  const isDesktop = useResponsive('up', 'lg', 'as', 'as');
  // Comment out notification count - replaced with pending orders count
  // const { totalRecords, status } = useAppSelector(
  //   (state) => state.notification
  // );
  const { count: pendingOrdersCount, status: pendingOrdersStatus } = useAppSelector(
    (state) => state.pendingOrdersCount
  );

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  }: any = useCollapseDrawer();
  const [sidebarItemState, setSidebarItemState] = useState<any>();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname, isDesktop]);

  useEffect(() => {
    if (!userRole) {
      localStorage.clear();
      dispatch(logoutUser());
    }
  }, [dispatch, userRole]);

  useEffect(() => {
    (async () => {
      const allItems: any = await handleRenderSidebarConfig();
      setSidebarItemState(allItems);
    })();
  }, [userRole, pendingOrdersCount]);

  useEffect(() => {
    // Fetch pending orders count instead of notifications
    // Only fetch for roles that show the order badge (admin, orderManager)
    const rolesWithOrderBadge = ['admin', 'orderManager'];
    
    if (pendingOrdersStatus === 'idle' && userRole && rolesWithOrderBadge.includes(userRole)) {
      dispatch(fetchPendingOrdersCount());
    }
  }, [dispatch, pendingOrdersStatus, userRole]);

  // Periodic refresh every 30 seconds
  useEffect(() => {
    const rolesWithOrderBadge = ['admin', 'orderManager'];
    
    if (userRole && rolesWithOrderBadge.includes(userRole)) {
      const interval = setInterval(() => {
        dispatch(refreshPendingOrdersCount());
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [dispatch, userRole]);

  const handleRenderSidebarConfig = () => {
    if (userRole === 'admin') {
      return [
        {
          items: [
            {
              title: 'Dashboard',
              path: '/',
              icon: <HomeIcon fontSize='small' />,
            },
            {
              title: 'Analytics Dashboard',
              path: '/analytics-dashboard',
              icon: <AnalyticsIcon fontSize='small' />,
            },
            {
              title: 'users',
              path: '/users',
              icon: <FaPeopleGroup fontSize='small' />,
            },
          ],
        },
        {
          items: [
            {
              title: 'Manage cake',
              icon: <MdAddBusiness fontSize='small' />,
              children: [
                {
                  title: 'shape',
                  path: '/shape',
                },
                {
                  title: 'color',
                  path: '/color',
                },
                {
                  title: 'flavor',
                  path: '/flavor',
                },
                {
                  title: 'cake box',
                  path: '/cake-box',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Manage toppings',
              icon: <MdOutlinePostAdd fontSize='small' />,
              children: [
                {
                  title: 'Toppings Category',
                  path: '/toppings-category',
                },
                {
                  title: 'Toppings Category Item',
                  path: '/toppings-category-item',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Manage Occasions',
              icon: <TbCategoryFilled fontSize='small' />,
              children: [
                {
                  title: 'Occasion',
                  path: '/occasion',
                },
                {
                  title: 'Occasion Cake',
                  path: '/occasion-cake',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Add-Ons',
              icon: <MdAddBusiness fontSize='small' />,
              children: [
                {
                  title: 'AddOn Category',
                  path: '/addon-category',
                },
                {
                  title: 'AddOn Product',
                  path: '/addon-product',
                },
                // {
                //   title: 'AddOn Product Variant',
                //   path: '/addon-product-variant',
                // },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Event Services',
              icon: <MdAddBusiness fontSize='small' />,
              children: [
                {
                  title: 'Vendors',
                  path: '/event-service-vendor',
                },
                {
                  title: 'Categories',
                  path: '/event-service-category',
                },
                {
                  title: 'Products',
                  path: '/event-service-product',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Bundle Products',
              icon: <MdAddBusiness fontSize='small' />,
              children: [
                {
                  title: 'Bundle Category',
                  path: '/bundle-category',
                },
                {
                  title: 'Bundle Subcategory',
                  path: '/bundle-subcategory',
                },
                {
                  title: 'Bundle Products',
                  path: '/bundle-product',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Orders',
              icon: (
                pendingOrdersCount > 0 ? (
                  <Badge
                    badgeContent={pendingOrdersCount}
                    color='error'
                    sx={pendingOrderBadgeSx}
                  >
                    <RiCake3Fill style={{ height: '1.3rem' }} />
                  </Badge>
                ) : (
                  <RiCake3Fill style={{ height: '1.3rem' }} />
                )
              ),
              children: [
                {
                  title: 'Order',
                  path: '/order',
                },
                {
                  title: 'Temporary Orders',
                  path: '/temp-order',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'General Settings',
              icon: <TbCategoryFilled fontSize='small' />,
              children: [
                {
                  title: 'Settings',
                  path: '/settings',
                },
                ...(isPartnerApiEnabled()
                  ? [
                      {
                        title: 'Partners',
                        path: '/partners',
                      },
                      {
                        title: 'Partner Audit',
                        path: '/partners/audit',
                      },
                    ]
                  : []),
                {
                  title: 'country',
                  path: '/country',
                },
                {
                  title: 'region',
                  path: '/region',
                },
                {
                  title: 'Promo Code',
                  path: '/promo-code',
                },
                {
                  title: 'Slider & Banner',
                  path: '/slider',
                },
                {
                  title: 'Delivery Time Slots',
                  path: '/delivery-time-slots',
                },
                {
                  title: 'Dynamic Word',
                  path: '/dynamic-word',
                },
                {
                  title: 'Reward & Referral',
                  path: '/reward-points',
                },
                {
                  title: 'Follow up Codes',
                  path: '/follow-up-codes',
                },
                {
                  title: 'Product Badges',
                  path: '/product-badges',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Notification',
              path: '/notification',
              icon: <IoIosNotifications fontSize='small' />,
            },
          ],
        },
        {
          items: [
            {
              title: 'Support',
              icon: <TbCategoryFilled fontSize='small' />,
              children: [
                {
                  title: 'FAQ',
                  path: '/faq',
                },
                // {
                //   title: 'Contact Form',
                //   path: '/contact-form',
                // },
              ],
            },
          ],
        },
      ];
    }
    if (userRole === 'contentManager') {
      return [
        {
          items: [
            {
              title: 'Dashboard',
              path: '/',
              icon: <HomeIcon fontSize='small' />,
            },
          ],
        },
        {
          items: [
            {
              title: 'Manage cake',
              icon: <MdAddBusiness fontSize='small' />,
              children: [
                {
                  title: 'shape',
                  path: '/shape',
                },
                {
                  title: 'color',
                  path: '/color',
                },
                {
                  title: 'cake box',
                  path: '/cake-box',
                },
                {
                  title: 'flavor',
                  path: '/flavor',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Manage toppings',
              icon: <MdOutlinePostAdd fontSize='small' />,
              children: [
                {
                  title: 'Toppings Category',
                  path: '/toppings-category',
                },
                {
                  title: 'Toppings Category Item',
                  path: '/toppings-category-item',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Manage Occasions',
              icon: <TbCategoryFilled fontSize='small' />,
              children: [
                {
                  title: 'Occasion',
                  path: '/occasion',
                },
                {
                  title: 'Occasion Cake',
                  path: '/occasion-cake',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Add-Ons',
              icon: <MdAddBusiness fontSize='small' />,
              children: [
                {
                  title: 'AddOn Category',
                  path: '/addon-category',
                },
                {
                  title: 'AddOn Product',
                  path: '/addon-product',
                },
                // {
                //   title: 'AddOn Product Variant',
                //   path: '/addon-product-variant',
                // },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Event Services',
              icon: <MdAddBusiness fontSize='small' />,
              children: [
                {
                  title: 'Vendors',
                  path: '/event-service-vendor',
                },
                {
                  title: 'Categories',
                  path: '/event-service-category',
                },
                {
                  title: 'Products',
                  path: '/event-service-product',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Bundle Products',
              icon: <MdAddBusiness fontSize='small' />,
              children: [
                {
                  title: 'Bundle Category',
                  path: '/bundle-category',
                },
                {
                  title: 'Bundle Subcategory',
                  path: '/bundle-subcategory',
                },
                {
                  title: 'Bundle Products',
                  path: '/bundle-product',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'General Settings',
              icon: <TbCategoryFilled fontSize='small' />,
              children: [
                {
                  title: 'Slider & Banner',
                  path: '/slider',
                },
                {
                  title: 'Delivery Time Slots',
                  path: '/delivery-time-slots',
                },
                {
                  title: 'Dynamic Word',
                  path: '/dynamic-word',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Support',
              icon: <TbCategoryFilled fontSize='small' />,
              children: [
                {
                  title: 'FAQ',
                  path: '/faq',
                },
                // {
                //   title: 'Contact Form',
                //   path: '/contact-form',
                // },
              ],
            },
          ],
        },
      ];
    }
    if (userRole === 'finance') {
      return [
        {
          items: [
            {
              title: 'Dashboard',
              path: '/',
              icon: <HomeIcon fontSize='small' />,
            },
            {
              title: 'Analytics Dashboard',
              path: '/analytics-dashboard',
              icon: <AnalyticsIcon fontSize='small' />,
            },
            {
              title: 'order',
              path: '/order',
              icon: <RiCake3Fill fontSize='small' />,
            },
          ],
        },
      ];
    }
    if (userRole === 'orderManager') {
      return [
        {
          items: [
            {
              title: 'Dashboard',
              path: '/',
              icon: <HomeIcon fontSize='small' />,
            },
            {
              title: 'Orders',
              icon: (
                pendingOrdersCount > 0 ? (
                  <Badge
                    badgeContent={pendingOrdersCount}
                    color='error'
                    sx={pendingOrderBadgeSx}
                  >
                    <RiCake3Fill style={{ height: '1.3rem' }} />
                  </Badge>
                ) : (
                  <RiCake3Fill style={{ height: '1.3rem' }} />
                )
              ),
              children: [
                {
                  title: 'Order',
                  path: '/order',
                },
                {
                  title: 'Temporary Orders',
                  path: '/temp-order',
                },
              ],
            },
          ],
        },
      ];
    }
    return [];
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        maxHeight: '100vh',
        '& .simplebar-content': {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: 'center' }),
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Logo isCollapse={isCollapse} />

          {isDesktop && !isCollapse && (
            <CollapseButton
              onToggleCollapse={onToggleCollapse}
              collapseClick={collapseClick}
            />
          )}
        </Stack>
      </Stack>

      <DrawerMenuListView
        menuConfig={sidebarItemState}
        isCollapse={isCollapse}
      />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        <Stack
          alignItems='center'
          spacing={3}
          sx={{ pt: 5, borderRadius: 2, position: 'relative' }}
        ></Stack>
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              height: '100%',
              bgcolor: '#FAF7F4',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant='persistent'
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              height: '100%',
              borderRightStyle: 'dashed',
              bgcolor: '#FAF7F4',
              transition: (theme: any) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur({ color: '#FAF7F4' }),
                boxShadow: (theme: any) => theme.customShadows.z24,
                backgroundColor: '#FAF7F4',
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
